import { isRequired } from 'cadenza/utils/is-required';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import { AttributeCategory } from 'cadenza/repository/attribute-category';
import { AttributeType } from 'cadenza/repository/attribute-type';

// This file is meant for generic attribute utilities, which are independent of a workbook redux store
// and thus can be used not only in a workbook page, but for example also in the management center.
// To have access to the workbook redux store, please use workbook-attribute-util.

/**
 * Checks the given attribute for acceptance. If accepted the attribute is not filtered out.
 *
 * @callback filterObjecttypes~AttributePredicate
 * @param {import('cadenza/workbook/api').AttributeDto} attribute - The attribute to check acceptance of
 * @return {boolean} True if excepted, else false
 */

/**
 * Filters the given object types.
 *
 * @param {ObjectType[]} objectTypes - Object types to filter
 * @param {object} options - Options - Only object types with attributes matching these criteria are
 *   included in the result. Without any criteria *all* object types are returned.
 * @param {AttributeCategory[]} [options.acceptedCategories] - Accepted attribute categories
 * @param {AttributeType[]} [options.acceptedTypes] - Accepted attribute types
 * @param {GeometryType[]} [options.acceptedGeometryTypes] - Accepted geometry types
 * @param {string[]} [options.acceptedKeywords] - Accepted keywords
 * @param {filterObjecttypes~AttributePredicate} [options.attributePredicate] - Attribute validity
 *   function. If invalid the attribute is removed.
 * @return {object[]} The filtered object types
 */
export function filterObjecttypes (
  objectTypes,
  {
    acceptedCategories = [],
    acceptedTypes = [],
    acceptedGeometryTypes = [],
    acceptedKeywords = [],
    attributePredicate
  } = {}) {
  const filteredObjecttypes = [];

  // Empty means all are allowed
  acceptedCategories = acceptedCategories.length ? acceptedCategories : AttributeCategory.values;
  acceptedTypes = acceptedTypes.length ? acceptedTypes : AttributeType.values;

  objectTypes.forEach(objectType => {
    const printNameMatchesKeywords = acceptedKeywords.length && match(objectType, acceptedKeywords);

    const clone = {
      ...objectType,
      attributes: {}
    };

    let attributes = objectType.attributes;
    attributes = attributes.filter(({ category, visible }) => visible && acceptedCategories.includes(category));

    if (acceptedGeometryTypes.length > 0) {
      // if we have an accepted GeometryType defined, we do not care about the type, only the
      // geometry type is relevant
      attributes = attributes.filter(({ relatedGeometryType }) => acceptedGeometryTypes.includes(relatedGeometryType));
    } else {
      // Hierarchies don't have a type, so we just keep them in list.
      attributes = attributes.filter(({ category, type }) =>
        category === AttributeCategory.HIERARCHY || acceptedTypes.includes(type));
    }

    // Filter by keywords in attribute printNames
    if (!printNameMatchesKeywords && acceptedKeywords.length) {
      attributes = matchAttributes(attributes, acceptedKeywords);
    }

    // Additional validation
    if (attributePredicate) {
      attributes = attributes.filter(attributePredicate);
    }

    // Only add if there are attributes still left
    if (attributes.length) {
      clone.attributes = attributes;
      filteredObjecttypes.push(clone);
    }
  });
  return filteredObjecttypes;
}

function matchAttributes (attributes, keywords) {
  const matchingAttributes = [];
  for (let i = 0; i < attributes.length; i++) {
    const attribute = attributes[i];
    if (attribute.category === AttributeCategory.HIERARCHY) {
      const hierarchyId = attribute.id;
      const hierarchyAttributes = [ attribute ];
      while (++i < attributes.length && attributes[i].hierarchyId === hierarchyId) {
        hierarchyAttributes.push(attributes[i]);
      }
      if (hierarchyAttributes.some(_ => match(_, keywords))) {
        matchingAttributes.push(...hierarchyAttributes);
      }
    } else if (match(attribute, keywords)) {
      matchingAttributes.push(attribute);
    }
  }
  return matchingAttributes;
}

function match ({ printName }, keywords) {
  const lowerCasePrintName = printName.toLowerCase();
  return keywords.some(keyword => lowerCasePrintName.includes(keyword));
}

export function isModified (attribute) {
  return Boolean(attribute.settings) && AttributeModificationKey.values.some(mod => {
    // Only consider modifications, which can be set in the modifications dialog.
    return mod !== AttributeModificationKey.USE_LOCAL_HIERARCHY_LEVEL && mod in attribute.settings;
  });
}

export function filterAttributes (
  attributes,
  {
    acceptedCategories = [],
    acceptedKeywords = []
  } = {}) {
  acceptedCategories = acceptedCategories.length ? acceptedCategories : AttributeCategory.values;

  if (acceptedKeywords.length === 0) {
    return attributes;
  }
  const filteredAttributes = [];
  attributes.forEach(attribute => {
    if (acceptedKeywords.length > 0) {
      const attributePrintNameMatchesKeywords = acceptedKeywords.length && match(attribute, acceptedKeywords);
      const objectTypePrintNameMatchesKeywords = acceptedKeywords.length && match({ printName: attribute.objectTypePrintName }, acceptedKeywords);
      if (!attributePrintNameMatchesKeywords && !objectTypePrintNameMatchesKeywords) {
        return;
      }
    }
    if (acceptedCategories.includes(attribute.category)) {
      filteredAttributes.push(attribute);
    }
  });

  return filteredAttributes;
}

/**
 * Finds an object type related to which the condition belongs to (via the first attribute). Throws
 * an error if none is found.
 *
 * @param {Objecttype[]} objecttypes - The objecttypes where to look
 * @param {import('cadenza/workbook/model/condition-dto').ConditionDto} condition - The condition to which the object type needs to be found
 * @return {Objecttype} object type the condition belongs to
 */
export function findObjecttype (objecttypes = isRequired(), condition = isRequired()) {
  const firstAttributeId = condition.attributeIds[0];
  for (const objectType of objecttypes) {
    for (const attribute of objectType.attributes) {
      if (attribute.id === firstAttributeId) {
        return objectType;
      }
    }
  }
  throw new Error('Could not find objectType containing attributeId: ' + firstAttributeId);
}
